import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router';
import { Game, PracticeShot, ShotType, getZoneFromShotType } from '../types/curling';
import { getGame, getPracticeSession } from '../utils/storage';
import { ArrowLeft, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { ScatterChart, Scatter, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ZAxis, ReferenceArea } from 'recharts';
import { SheetVisualizer } from '../components/SheetVisualizer';
import { fitSmoothCurve } from '../utils/curveFit';

interface SheetData {
  totalShots: number;
  avgCurl: number;
  avgTime: number;
  curlTrend: 'increases' | 'decreases' | 'stable';
  timeTrend: 'speeds-up' | 'slows-down' | 'stable';
  curlChange: number;
  timeChange: number;
  curlByEnd: { end: number; avgCurl: number }[];
  timeByEnd: { end: number; avgTime: number }[];
  zoneData: ZoneData[];
  broomPositionDataAway: BroomPositionData[];
  broomPositionDataHome: BroomPositionData[];
}

interface ZoneData {
  zone: number;
  label: string;
  totalShots: number;
  avgCurl: number;
  avgTime: number;
  curlTrend: 'increases' | 'decreases' | 'stable';
  timeTrend: 'speeds-up' | 'slows-down' | 'stable';
  curlChange: number;
  timeChange: number;
}

interface BroomPositionData {
  zone: number;
  label: string;
  cwShots: number;
  ccwShots: number;
  totalShots: number;
  cwAvgCurl: number;
  ccwAvgCurl: number;
}

// Horizontal regions for broom position (feet from center)
const horizontalRegions = [
  { minPos: -6, maxPos: -5, label: '6 ft Left' },
  { minPos: -5, maxPos: -4, label: '5 ft Left' },
  { minPos: -4, maxPos: -3, label: '4 ft Left' },
  { minPos: -3, maxPos: -2, label: '3 ft Left' },
  { minPos: -2, maxPos: -1, label: '2 ft Left' },
  { minPos: -1, maxPos: 0, label: '1 ft Left' },
  { minPos: 0, maxPos: 1, label: '1 ft Right' },
  { minPos: 1, maxPos: 2, label: '2 ft Right' },
  { minPos: 2, maxPos: 3, label: '3 ft Right' },
  { minPos: 3, maxPos: 4, label: '4 ft Right' },
  { minPos: 4, maxPos: 5, label: '5 ft Right' },
  { minPos: 5, maxPos: 6, label: '6 ft Right' },
];

// Broom-position axis (feet from center): the real -6 to +6 range
// broomPosition is stored in, plus a bookend tick on each side for the edge
// of the sheet — 15 positions total, matching how ShotVisualizer/ShotTracker
// already draw the sheet width.
const POSITION_TICKS = [-7, -6, -5, -4, -3, -2, -1, 0, 1, 2, 3, 4, 5, 6, 7];
const positionTickLabel = (v: number): string => {
  if (v <= -7) return 'Left Edge';
  if (v >= 7) return 'Right Edge';
  if (v === 0) return 'CL';
  return `${v > 0 ? '+' : ''}${v}ft`;
};

// Zone labels for the sheet (vertical positions)
const zoneLabels = [
  { zone: 0, label: 'Hogged' },
  { zone: 1, label: 'High Guard' },
  { zone: 2, label: 'Mid Guard' },
  { zone: 3, label: 'Tight Guard' },
  { zone: 4, label: 'Top Twelve' },
  { zone: 5, label: 'Top Eight' },
  { zone: 6, label: 'Top Four' },
  { zone: 7, label: 'Tee Line' },
  { zone: 8, label: 'Back Four' },
  { zone: 9, label: 'Back Eight' },
  { zone: 10, label: 'Back Twelve' },
  { zone: 11, label: 'Hack' },
  { zone: 12, label: 'Board' },
  { zone: 13, label: 'Control' },
  { zone: 14, label: 'Normal' },
  { zone: 15, label: 'Firm' },
];

export function SheetMap() {
  const navigate = useNavigate();
  const location = useLocation();
  const { gameId } = useParams<{ gameId: string }>();
  const [game, setGame] = useState<Game | null>(null);
  const [sheetData, setSheetData] = useState<SheetData | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [practiceTimeData, setPracticeTimeData] = useState<{ id: string; zone: number; zoneLabel: string; time: number }[]>([]);
  const [gameTimeData, setGameTimeData] = useState<{ id: string; zone: number; zoneLabel: string; time: number }[]>([]);
  const [curlByPositionAwayData, setCurlByPositionAwayData] = useState<{ id: string; position: number; curl: number }[]>([]);
  const [curlByPositionHomeData, setCurlByPositionHomeData] = useState<{ id: string; position: number; curl: number }[]>([]);
  const [practiceCount, setPracticeCount] = useState<number>(0);

  useEffect(() => {
    if (!gameId) return;
    let cancelled = false;

    // The game and its warm-up shots are independent reads, so fetch them together.
    Promise.all([getGame(gameId), getPracticeSession(gameId)])
      .then(([foundGame, session]) => {
        if (cancelled || !foundGame) return;
        setGame(foundGame);
        calculateSheetData(foundGame, session.shots ?? []);
        // Find the most recent shot timestamp
        const mostRecentTimestamp = findMostRecentShot(foundGame);
        if (mostRecentTimestamp) {
          setLastUpdated(new Date(mostRecentTimestamp));
        }
      })
      .catch(error => console.error('Failed to load sheet map data:', error));

    return () => {
      cancelled = true;
    };
  }, [gameId]);

  const findMostRecentShot = (game: Game): number | null => {
    let mostRecent: number | null = null;
    game.ends.forEach(end => {
      end.shots.forEach(shot => {
        if (shot.timestamp && (!mostRecent || shot.timestamp > mostRecent)) {
          mostRecent = shot.timestamp;
        }
      });
    });
    return mostRecent;
  };

  const calculateSheetData = (game: Game, practiceShots: PracticeShot[]) => {
    // Initialize zone data tracking (for all shots)
    const zoneCurlsByEnd: Map<number, { end: number; avgCurl: number }[]> = new Map();
    const zoneTimesByEnd: Map<number, { end: number; avgTime: number }[]> = new Map();
    const zoneShotsData: Map<number, { curls: number[]; times: number[] }> = new Map();

    // Initialize all zones
    for (let i = 0; i <= 16; i++) {
      zoneCurlsByEnd.set(i, []);
      zoneTimesByEnd.set(i, []);
      zoneShotsData.set(i, { curls: [], times: [] });
    }

    // Add practice shots to zone data
    practiceShots.forEach(practiceShot => {
      // Extract zone number from zone string (e.g., "7. Tee Line" -> 7)
      const zoneMatch = practiceShot.zone.match(/^(\d+)\./);
      if (zoneMatch) {
        const zoneNum = parseInt(zoneMatch[1]);
        zoneShotsData.get(zoneNum)?.curls.push(practiceShot.curl);
        // Only collect times > 0 (ignore 0.0s times)
        if (practiceShot.time > 0) {
          zoneShotsData.get(zoneNum)?.times.push(practiceShot.time);
        }
      }
    });

    // Collect data from all shots in the game
    game.ends.forEach(end => {
      const zoneEndCurls: Map<number, number[]> = new Map();
      const zoneEndTimes: Map<number, number[]> = new Map();

      // Initialize zone end tracking
      for (let i = 0; i <= 16; i++) {
        zoneEndCurls.set(i, []);
        zoneEndTimes.set(i, []);
      }

      end.shots.forEach(shot => {
        if (!shot.skipped) {
          // Track by zone (actualPosition) - all shots
          const zone = shot.actualPosition;
          zoneShotsData.get(zone)?.curls.push(shot.curl);
          zoneEndCurls.get(zone)?.push(shot.curl);
          // Only collect times > 0 (ignore 0.0s times)
          if (shot.time > 0) {
            zoneShotsData.get(zone)?.times.push(shot.time);
            zoneEndTimes.get(zone)?.push(shot.time);
          }
        }
      });

      // Calculate zone averages per end (all shots)
      for (let i = 0; i <= 16; i++) {
        const zoneCurls = zoneEndCurls.get(i) || [];
        const zoneTimes = zoneEndTimes.get(i) || [];

        if (zoneCurls.length > 0) {
          zoneCurlsByEnd.get(i)?.push({
            end: end.number,
            avgCurl: zoneCurls.reduce((sum, c) => sum + c, 0) / zoneCurls.length
          });
        }

        if (zoneTimes.length > 0) {
          zoneTimesByEnd.get(i)?.push({
            end: end.number,
            avgTime: zoneTimes.reduce((sum, t) => sum + t, 0) / zoneTimes.length
          });
        }
      }
    });

    // Check if we have any shots at all
    const totalShots = Array.from(zoneShotsData.values())
      .reduce((sum, data) => sum + data.curls.length, 0);

    if (totalShots === 0) {
      setSheetData(null);
      return;
    }

    // Calculate zone data
    const zoneData: ZoneData[] = zoneLabels.map(zoneLabel => {
      const zoneShots = zoneShotsData.get(zoneLabel.zone);
      const zoneCurls = zoneShots?.curls || [];
      const zoneTimes = zoneShots?.times || [];
      const zoneCurlData = zoneCurlsByEnd.get(zoneLabel.zone) || [];
      const zoneTimeData = zoneTimesByEnd.get(zoneLabel.zone) || [];

      let avgCurl = 0;
      let avgTime = 0;
      let curlTrend: 'increases' | 'decreases' | 'stable' = 'stable';
      let timeTrend: 'speeds-up' | 'slows-down' | 'stable' = 'stable';
      let curlChange = 0;
      let timeChange = 0;

      if (zoneCurls.length > 0) {
        avgCurl = zoneCurls.reduce((sum, c) => sum + c, 0) / zoneCurls.length;
      }

      if (zoneTimes.length > 0) {
        avgTime = zoneTimes.reduce((sum, t) => sum + t, 0) / zoneTimes.length;
      }

      // Calculate zone trends
      if (zoneCurlData.length >= 2) {
        const firstZoneCurl = zoneCurlData[0].avgCurl;
        const lastZoneCurl = zoneCurlData[zoneCurlData.length - 1].avgCurl;
        curlChange = lastZoneCurl - firstZoneCurl;

        if (Math.abs(curlChange) >= 0.5) {
          curlTrend = curlChange > 0 ? 'increases' : 'decreases';
        }
      }

      if (zoneTimeData.length >= 2) {
        const firstZoneTime = zoneTimeData[0].avgTime;
        const lastZoneTime = zoneTimeData[zoneTimeData.length - 1].avgTime;
        timeChange = lastZoneTime - firstZoneTime;

        if (Math.abs(timeChange) >= 0.2) {
          // A fast (low-friction) rock needs less effort to reach the same call, so
          // it takes longer to cross the timed zone — a rising time means the ice is
          // speeding up, not slowing down.
          timeTrend = timeChange > 0 ? 'speeds-up' : 'slows-down';
        }
      }

      return {
        zone: zoneLabel.zone,
        label: zoneLabel.label,
        totalShots: zoneCurls.length,
        avgCurl,
        avgTime,
        curlTrend,
        timeTrend,
        curlChange,
        timeChange,
      };
    });

    // Calculate summary metrics from ZONES 6-8 (Top Four, Tee Line, Back Four)
    const summaryZones = zoneData.filter(z => z.zone >= 6 && z.zone <= 8 && z.totalShots > 0);

    let avgCurl = 0;
    let avgTime = 0;
    let totalSummaryShots = 0;

    if (summaryZones.length > 0) {
      // Average the averages, weighted by shot count
      const totalWeightedCurl = summaryZones.reduce((sum, z) => sum + (z.avgCurl * z.totalShots), 0);
      totalSummaryShots = summaryZones.reduce((sum, z) => sum + z.totalShots, 0);

      avgCurl = totalWeightedCurl / totalSummaryShots;

      // Calculate Estimated Tee Line Weight
      // Weighted average: ((zone 6 time - 0.2) * zone 6 shots + zone 7 time * zone 7 shots + (zone 8 time + 0.2) * zone 8 shots) / total shots
      const zone6 = zoneData.find(z => z.zone === 6);
      const zone7 = zoneData.find(z => z.zone === 7);
      const zone8 = zoneData.find(z => z.zone === 8);

      let totalWeightedTime = 0;
      let totalTimeShots = 0;

      if (zone6 && zone6.totalShots > 0) {
        totalWeightedTime += (zone6.avgTime - 0.2) * zone6.totalShots;
        totalTimeShots += zone6.totalShots;
      }
      if (zone7 && zone7.totalShots > 0) {
        totalWeightedTime += zone7.avgTime * zone7.totalShots;
        totalTimeShots += zone7.totalShots;
      }
      if (zone8 && zone8.totalShots > 0) {
        totalWeightedTime += (zone8.avgTime + 0.2) * zone8.totalShots;
        totalTimeShots += zone8.totalShots;
      }

      if (totalTimeShots > 0) {
        avgTime = totalWeightedTime / totalTimeShots;
      }
    }

    // Calculate overall trends from zones 6-8
    let curlTrend: 'increases' | 'decreases' | 'stable' = 'stable';
    let timeTrend: 'speeds-up' | 'slows-down' | 'stable' = 'stable';
    let curlChange = 0;
    let timeChange = 0;

    // Collect end-by-end data for zones 6-8
    const allEnds = new Set<number>();
    for (let zone = 6; zone <= 8; zone++) {
      const zoneEndCurls = zoneCurlsByEnd.get(zone) || [];
      zoneEndCurls.forEach(data => allEnds.add(data.end));
    }

    const sortedEnds = Array.from(allEnds).sort((a, b) => a - b);
    const curlByEnd: { end: number; avgCurl: number }[] = [];
    const timeByEnd: { end: number; avgTime: number }[] = [];

    sortedEnds.forEach(endNum => {
      const endCurls: number[] = [];
      const endTimes: number[] = [];

      for (let zone = 6; zone <= 8; zone++) {
        const zoneEndCurlData = zoneCurlsByEnd.get(zone) || [];
        const zoneEndTimeData = zoneTimesByEnd.get(zone) || [];

        const curlData = zoneEndCurlData.find(d => d.end === endNum);
        const timeData = zoneEndTimeData.find(d => d.end === endNum);

        if (curlData) endCurls.push(curlData.avgCurl);
        if (timeData) endTimes.push(timeData.avgTime);
      }

      if (endCurls.length > 0) {
        curlByEnd.push({
          end: endNum,
          avgCurl: endCurls.reduce((sum, c) => sum + c, 0) / endCurls.length
        });
      }

      if (endTimes.length > 0) {
        timeByEnd.push({
          end: endNum,
          avgTime: endTimes.reduce((sum, t) => sum + t, 0) / endTimes.length
        });
      }
    });

    // Calculate trends
    if (curlByEnd.length >= 2) {
      const firstEndCurl = curlByEnd[0].avgCurl;
      const lastEndCurl = curlByEnd[curlByEnd.length - 1].avgCurl;
      curlChange = lastEndCurl - firstEndCurl;

      if (Math.abs(curlChange) >= 0.5) {
        curlTrend = curlChange > 0 ? 'increases' : 'decreases';
      }
    }

    if (timeByEnd.length >= 2) {
      const firstEndTime = timeByEnd[0].avgTime;
      const lastEndTime = timeByEnd[timeByEnd.length - 1].avgTime;
      timeChange = lastEndTime - firstEndTime;

      if (Math.abs(timeChange) >= 0.2) {
        // Same reasoning as the per-zone trend above: a rising time means the ice is
        // speeding up (less friction, gentler throws take longer), not slowing down.
        timeTrend = timeChange > 0 ? 'speeds-up' : 'slows-down';
      }
    }

    // Calculate broom position data split by Away/Home ends
    const makeHorizontalCounts = () =>
      horizontalRegions.map(region => ({
        label: region.label,
        minPos: region.minPos,
        maxPos: region.maxPos,
        cw: 0,
        ccw: 0,
        cwCurlSum: 0,
        ccwCurlSum: 0,
        cwCurlCount: 0,
        ccwCurlCount: 0,
      }));

    const horizontalCountsAway = makeHorizontalCounts();
    const horizontalCountsHome = makeHorizontalCounts();

    // Count shots by horizontal broom position and rotation, separated by away/home
    // Filter to only shots aimed at the house (zones 4-10)
    game.ends.forEach(end => {
      const isAway = end.number % 2 !== 0;
      const horizontalCounts = isAway ? horizontalCountsAway : horizontalCountsHome;

      end.shots.forEach(shot => {
        if (!shot.skipped) {
          // Get intended zone from shot type
          const intendedZone = getZoneFromShotType(shot.shotType);

          // Only count shots aimed at the house (zones 4-10)
          if (intendedZone >= 4 && intendedZone <= 10) {
            const broomPos = shot.broomPosition;

            // Find which horizontal region this broom position belongs to
            const region = horizontalCounts.find(r => broomPos >= r.minPos && broomPos < r.maxPos);

            if (region) {
              if (shot.turn === 'clockwise') {
                region.cw++;
                // Only count curl for zones 5, 6, or 7
                if (intendedZone >= 5 && intendedZone <= 7) {
                  region.cwCurlSum += shot.curl;
                  region.cwCurlCount++;
                }
              } else {
                region.ccw++;
                // Only count curl for zones 5, 6, or 7
                if (intendedZone >= 5 && intendedZone <= 7) {
                  region.ccwCurlSum += shot.curl;
                  region.ccwCurlCount++;
                }
              }
            }
          }
        }
      });
    });

    // Build broom position data for Away and Home ends
    const buildBroomPositionData = (
      horizontalCounts: typeof horizontalCountsAway
    ): BroomPositionData[] =>
      horizontalCounts.map((region, index) => ({
        zone: index,
        label: region.label,
        cwShots: region.cw,
        ccwShots: region.ccw,
        totalShots: region.cw + region.ccw,
        cwAvgCurl: region.cwCurlCount > 0 ? region.cwCurlSum / region.cwCurlCount : 0,
        ccwAvgCurl: region.ccwCurlCount > 0 ? region.ccwCurlSum / region.ccwCurlCount : 0,
      }));

    const broomPositionDataAway = buildBroomPositionData(horizontalCountsAway);
    const broomPositionDataHome = buildBroomPositionData(horizontalCountsHome);

    // Prepare scatter plot data (time by zone; curl plots by broom position instead, split
    // by end parity — ice and curl behavior are typically different away vs. home, so these
    // shouldn't be blended into one trend)
    const timeScatterData: { id: string; zone: number; zoneLabel: string; time: number }[] = [];
    const curlByPositionAway: { id: string; position: number; curl: number }[] = [];
    const curlByPositionHome: { id: string; position: number; curl: number }[] = [];
    let timeId = 0;
    let positionAwayId = 0;
    let positionHomeId = 0;

    // Add practice shots to scatter data
    practiceShots.forEach(practiceShot => {
      const zoneMatch = practiceShot.zone.match(/^(\d+)\./);
      if (zoneMatch) {
        const zoneNum = parseInt(zoneMatch[1]);
        const zoneLbl = zoneLabels.find(z => z.zone === zoneNum)?.label ?? `Zone ${zoneNum}`;
        if (practiceShot.time > 0) {
          timeScatterData.push({
            id: `time-${timeId++}`,
            zone: zoneNum,
            zoneLabel: zoneLbl,
            time: practiceShot.time,
          });
        }
      }
    });

    // Add game shots to scatter data
    game.ends.forEach(end => {
      const isAway = end.number % 2 !== 0;
      end.shots.forEach(shot => {
        if (!shot.skipped) {
          const zoneLbl = zoneLabels.find(z => z.zone === shot.actualPosition)?.label ?? `Zone ${shot.actualPosition}`;
          if (shot.time > 0) {
            timeScatterData.push({
              id: `time-${timeId++}`,
              zone: shot.actualPosition,
              zoneLabel: zoneLbl,
              time: shot.time,
            });
          }
          // Every shot's curl against its actual broom position, regardless
          // of target zone — the position-based counterpart to scatterData
          // above, kept separate per end since ice behaves differently
          // away vs. home.
          const posEntry = {
            id: `curlpos-${isAway ? positionAwayId++ : positionHomeId++}`,
            position: shot.broomPosition,
            curl: shot.curl,
          };
          if (isAway) {
            curlByPositionAway.push(posEntry);
          } else {
            curlByPositionHome.push(posEntry);
          }
        }
      });
    });

    // Split data into practice and game shots based on count
    const practiceCount = practiceShots.length;
    setPracticeCount(practiceCount);
    setPracticeTimeData(timeScatterData.slice(0, practiceCount));
    setGameTimeData(timeScatterData.slice(practiceCount));
    setCurlByPositionAwayData(curlByPositionAway);
    setCurlByPositionHomeData(curlByPositionHome);

    setSheetData({
      totalShots: totalSummaryShots, // Total shots in zones 4-10
      avgCurl,
      avgTime,
      curlTrend,
      timeTrend,
      curlChange,
      timeChange,
      curlByEnd, // Zones 4-10 by end
      timeByEnd, // Zones 4-10 by end
      zoneData,
      broomPositionDataAway,
      broomPositionDataHome,
    });
  };

  const curlDisplay = (v: number) => `${Math.abs(v).toFixed(1)} ft`;

  const getTrendColor = (trend: string): string => {
    if (trend === 'stable') return 'text-slate-400';
    if (trend === 'increases' || trend === 'slows-down') return 'text-orange-300';
    return 'text-blue-300';
  };

  const getTrendBgColor = (trend: string): string => {
    if (trend === 'stable') return 'bg-white/10';
    if (trend === 'increases' || trend === 'slows-down') return 'bg-orange-500/20';
    return 'bg-blue-500/20';
  };

  const formatTrend = (trend: string): string => {
    return trend.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  };

  const getTrendIcon = (trend: string) => {
    if (trend === 'stable') return <Minus className="w-5 h-5" />;
    if (trend === 'increases' || trend === 'slows-down') return <TrendingUp className="w-5 h-5" />;
    return <TrendingDown className="w-5 h-5" />;
  };

  if (!game) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900 p-6">
        <div className="max-w-7xl mx-auto">
          <p className="text-blue-300">Loading...</p>
        </div>
      </div>
    );
  }

  // Small trend chart paired with an end-by-end averages list: the actual
  // per-end average as dots, plus a cubic spline through those same points
  // so the change end-to-end reads as a shape rather than just a list of
  // numbers. No tooltip — the exact values are already in the list above it.
  const renderEndTrend = <T extends { end: number }>(data: T[], valueKey: keyof T, unit: string, yLabel: string) => {
    if (data.length === 0) return null;
    const points = data.map(d => ({ x: d.end, y: d[valueKey] as unknown as number }));
    const curve = fitSmoothCurve(points);
    if (!curve) return null;

    const minEnd = points[0].x;
    const maxEnd = points[points.length - 1].x;
    const trendData = Array.from({ length: 61 }, (_, i) => {
      const end = minEnd + (i / 60) * (maxEnd - minEnd || 1);
      return { end, value: curve(end) };
    });
    const actualData = points.map(p => ({ end: p.x, value: p.y }));
    const endTicks = Array.from({ length: maxEnd - minEnd + 1 }, (_, i) => minEnd + i);

    // Fit the y-axis to the data instead of defaulting to a 0-based scale —
    // time in particular sits in a narrow ~13-16s band, which a 0-based axis
    // would compress into a sliver and make the trend hard to read.
    const allValues = [...trendData.map(d => d.value), ...actualData.map(d => d.value)];
    const valueMin = Math.min(...allValues);
    const valueMax = Math.max(...allValues);
    const pad = Math.max((valueMax - valueMin) * 0.15, 0.5);

    return (
      <ResponsiveContainer width="100%" height={280}>
        <ScatterChart margin={{ top: 20, right: 30, bottom: 40, left: 60 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="end" type="number" domain={[minEnd, maxEnd]} ticks={endTicks} allowDecimals={false}
            tick={{ fill: '#ffffff' }}
            label={{ value: 'End', position: 'insideBottom', offset: -10, fill: '#ffffff' }} />
          <YAxis dataKey="value" unit={` ${unit}`} domain={[valueMin - pad, valueMax + pad]}
            tickFormatter={(v: number) => v.toFixed(1)}
            tick={{ fill: '#ffffff' }}
            label={{ value: yLabel, angle: -90, position: 'insideLeft', offset: -10, fill: '#ffffff' }} />
          <Scatter
            name="Trend"
            data={trendData}
            fill="#ef4444"
            line={{ stroke: '#ef4444', strokeWidth: 2 }}
            lineType="joint"
            shape={(props: any) => <circle cx={props.cx} cy={props.cy} r={0} fill="none" />}
            isAnimationActive={false}
          />
          <Scatter name="Average" data={actualData} fill="#3b82f6" isAnimationActive={false} />
        </ScatterChart>
      </ResponsiveContainer>
    );
  };

  // Curl-by-position card (scatter + smoothed trend line) for one end parity
  // — away and home ends are kept separate rather than blended, since ice
  // and curl behavior are typically different between them.
  const renderCurlByPathCard = (data: { id: string; position: number; curl: number }[], endLabel: string) => {
    if (data.length === 0) return null;

    // A cubic spline through each foot of broom position's average curl,
    // rather than one straight line (or a spline through hundreds of
    // individual noisy shots, which would oscillate wildly). Bucket to the
    // nearest foot within [-6, 6] — the real range broomPosition is recorded
    // in — then fit through those per-foot averages.
    const bucketed = new Map<number, number[]>();
    data.forEach(d => {
      const bucket = Math.max(-6, Math.min(6, Math.round(d.position)));
      if (!bucketed.has(bucket)) bucketed.set(bucket, []);
      bucketed.get(bucket)!.push(d.curl);
    });
    const curlByPosition = Array.from(bucketed.entries())
      .map(([position, curls]) => ({ x: position, y: curls.reduce((sum, c) => sum + c, 0) / curls.length }));
    const curlCurve = fitSmoothCurve(curlByPosition);

    if (!curlCurve) return null;

    // Fit the y-axis to the actual curl values instead of the recharts
    // default (a 0-based "nice number" scale), same as the other charts on
    // this page.
    const allCurls = data.map(d => d.curl);
    const curlMin = Math.min(...allCurls);
    const curlMax = Math.max(...allCurls);
    const curlPad = Math.max((curlMax - curlMin) * 0.15, 0.5);

    const trendCurls = curlByPosition.map(p => p.y);
    const trendCurlMin = Math.min(...trendCurls);
    const trendCurlMax = Math.max(...trendCurls);
    const trendCurlPad = Math.max((trendCurlMax - trendCurlMin) * 0.15, 0.5);

    return (
      <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6 mt-6">
        <h3 className="text-lg font-semibold text-white mb-4">Curl by Path — {endLabel} End</h3>
        <p className="text-xs text-slate-400 mb-4">Distribution of curl amounts across broom position, left to right</p>
        <ResponsiveContainer width="100%" height={450}>
          <ScatterChart margin={{ top: 20, right: 30, bottom: 40, left: 20 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="position"
              name="Position"
              type="number"
              domain={[-7, 7]}
              ticks={POSITION_TICKS}
              tickFormatter={positionTickLabel}
              tick={{ fill: '#ffffff' }}
              label={{ value: 'Broom Position', position: 'insideBottom', offset: -10, fill: '#ffffff' }}
            />
            <YAxis
              dataKey="curl"
              name="Curl"
              unit=" ft"
              domain={[curlMin - curlPad, curlMax + curlPad]}
              tickFormatter={(v: number) => v.toFixed(1)}
              tick={{ fill: '#ffffff' }}
              label={{ value: 'Curl (feet)', angle: -90, position: 'insideLeft', fill: '#ffffff' }}
            />
            <ZAxis range={[60, 60]} />
            <Tooltip
              cursor={{ strokeDasharray: '3 3' }}
              content={({ payload }) => {
                if (payload && payload.length > 0) {
                  const p = payload[0].payload;
                  return (
                    <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-lg">
                      <p className="text-sm text-gray-600">Position: {p.position.toFixed(1)} ft</p>
                      <p className="text-sm text-gray-600">Curl: {curlDisplay(p.curl)}</p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Scatter
              name="Game Shots"
              data={data}
              fill="#3b82f6"
              fillOpacity={0.6}
              isAnimationActive={false}
            />
          </ScatterChart>
        </ResponsiveContainer>

        {/* Best Fit Line Chart */}
        <div className="mt-4">
          <h4 className="text-md font-semibold text-white mb-2">Curl Trend Line</h4>
          <p className="text-xs text-slate-400 mb-4">Smoothed curve through each foot of broom position's average curl</p>
          <ResponsiveContainer width="100%" height={300}>
            <ScatterChart margin={{ top: 20, right: 30, bottom: 40, left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" />
              {/* House-ring shading — real feet, so no scaling tricks: the
                  twelve-, eight-, four-foot rings and the button sit at
                  their literal radii either side of 0. */}
              <ReferenceArea x1={-6} x2={-4} fill="#2563eb" fillOpacity={0.25} ifOverflow="visible" />
              <ReferenceArea x1={4} x2={6} fill="#2563eb" fillOpacity={0.25} ifOverflow="visible" />
              <ReferenceArea x1={-4} x2={-2} fill="#e2e8f0" fillOpacity={0.14} ifOverflow="visible" />
              <ReferenceArea x1={2} x2={4} fill="#e2e8f0" fillOpacity={0.14} ifOverflow="visible" />
              <ReferenceArea x1={-2} x2={-0.5} fill="#dc2626" fillOpacity={0.3} ifOverflow="visible" />
              <ReferenceArea x1={0.5} x2={2} fill="#dc2626" fillOpacity={0.3} ifOverflow="visible" />
              <ReferenceArea x1={-0.5} x2={0.5} fill="#ffffff" fillOpacity={0.55} ifOverflow="visible" />
              <XAxis
                dataKey="position"
                name="Position"
                type="number"
                domain={[-7, 7]}
                ticks={POSITION_TICKS}
                tickFormatter={positionTickLabel}
                tick={{ fill: '#ffffff' }}
                label={{ value: 'Broom Position', position: 'insideBottom', offset: -10, fill: '#ffffff' }}
              />
              <YAxis
                dataKey="curl"
                name="Curl"
                unit=" ft"
                domain={[trendCurlMin - trendCurlPad, trendCurlMax + trendCurlPad]}
                tickFormatter={(v: number) => v.toFixed(1)}
                tick={{ fill: '#ffffff' }}
                label={{ value: 'Curl (feet)', angle: -90, position: 'insideLeft', fill: '#ffffff' }}
              />
              <Tooltip
                cursor={{ strokeDasharray: '3 3' }}
                content={({ payload }) => {
                  if (payload && payload.length > 0) {
                    const p = payload[0].payload;
                    return (
                      <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-lg">
                        <p className="text-sm text-gray-600">Position: {p.position.toFixed(1)} ft</p>
                        <p className="text-sm text-gray-600">Predicted Curl: {p.curl.toFixed(2)} ft</p>
                      </div>
                    );
                  }
                  return null;
                }}
              />
              <Scatter
                name="Best Fit Line"
                data={Array.from({ length: 121 }, (_, i) => {
                  const position = -6 + (i / 120) * 12;
                  return { position, curl: curlCurve(position) };
                })}
                fill="#ef4444"
                line={{ stroke: '#ef4444', strokeWidth: 3 }}
                lineType="joint"
                shape={(props: any) => {
                  return <circle cx={props.cx} cy={props.cy} r={0} fill="none" />;
                }}
                isAnimationActive={false}
              />
            </ScatterChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  // Reusable broom position table renderer
  const renderBroomPositionTable = (data: BroomPositionData[]) => (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead className="bg-white/5 border-b border-white/15">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-semibold text-blue-300 uppercase tracking-wider">Horizontal Position</th>
            <th className="px-6 py-3 text-center text-xs font-semibold text-blue-300 uppercase tracking-wider">CW Shots</th>
            <th className="px-6 py-3 text-center text-xs font-semibold text-blue-300 uppercase tracking-wider">CW Avg Curl (to 4 foot)</th>
            <th className="px-6 py-3 text-center text-xs font-semibold text-blue-300 uppercase tracking-wider">CCW Shots</th>
            <th className="px-6 py-3 text-center text-xs font-semibold text-blue-300 uppercase tracking-wider">CCW Avg Curl (to 4 foot)</th>
            <th className="px-6 py-3 text-center text-xs font-semibold text-blue-300 uppercase tracking-wider">Total</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/10">
          {data.map((zone, idx) => (
            <tr
              key={zone.zone}
              className={`${zone.totalShots === 0 ? 'opacity-40' : 'hover:bg-white/5'} ${idx % 2 === 0 ? 'bg-white/5' : ''}`}
            >
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm font-medium text-white">{zone.label}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-center">
                <div className="text-sm font-semibold text-white">{zone.cwShots}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-center">
                <div className="text-sm text-white">
                  {zone.cwShots > 0 ? curlDisplay(zone.cwAvgCurl) : '-'}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-center">
                <div className="text-sm font-semibold text-white">{zone.ccwShots}</div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-center">
                <div className="text-sm text-white">
                  {zone.ccwShots > 0 ? curlDisplay(zone.ccwAvgCurl) : '-'}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap text-center">
                <div className="text-sm font-bold text-white">{zone.totalShots}</div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-950 via-blue-900 to-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6 mb-6">
          <button
            onClick={() => {
              // Return to whichever page the user actually came from (history, the
              // game summary, a club/sheet game list, ...) rather than guessing.
              // Only synthesize a destination if there's no in-app page to pop back to.
              if (location.key !== 'default') {
                navigate(-1);
              } else {
                navigate(`/CurlingCodex/game/${gameId}`);
              }
            }}
            className="flex items-center gap-2 text-blue-300 hover:text-blue-200 mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          <div className="flex items-center justify-between flex-wrap gap-4 mb-2">
            <h1 className="text-2xl font-bold text-white">Sheet Map</h1>
            <SheetVisualizer game={game} />
          </div>
          <div className="flex gap-3 text-sm text-blue-300">
            {game.curlingClub && <span>{game.curlingClub}</span>}
            {game.curlingClub && game.sheetNumber && <span>•</span>}
            {game.sheetNumber && <span>Sheet {game.sheetNumber}</span>}
            {game.sheetNumber && lastUpdated && <span>•</span>}
            {lastUpdated && (
              <span>Last updated: {lastUpdated.toLocaleDateString()} {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
            )}
          </div>
          <p className="text-blue-300 mt-2">
            Overall ice behavior and conditions on this sheet during the game
          </p>
        </div>

        {/* Sheet Data */}
        {sheetData ? (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {/* Estimated Tee Line Weight */}
              <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-xl shadow-xl shadow-black/20 p-6">
                <p className="text-sm text-blue-300 mb-2">Estimated Tee Line Weight</p>
                <p className="text-3xl font-bold text-white mb-1">{sheetData.avgTime.toFixed(2)} sec</p>
                <p className="text-xs text-slate-400">Hog to Hog</p>
              </div>

              {/* Average Curl into 4ft */}
              <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-xl shadow-xl shadow-black/20 p-6">
                <p className="text-sm text-blue-300 mb-2">Average Curl into 4ft</p>
                <p className="text-3xl font-bold text-white mb-1">{curlDisplay(sheetData.avgCurl)}</p>
                <p className="text-xs text-slate-400">Draws to the 4-foot, any side or end</p>
              </div>
            </div>

            {/* Time by End */}
            <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6 mt-6">
              <h3 className="text-lg font-semibold text-white mb-4">Time by End (4-Foot)</h3>
              <p className="text-xs text-slate-400 mb-3">Average time for 4-foot zones per end</p>
              <div className="space-y-2 mb-4">
                {sheetData.timeByEnd.map((data) => (
                  <div key={data.end} className="flex items-center justify-between py-2 border-b border-white/10 last:border-0">
                    <span className="text-sm font-medium text-white">End {data.end}</span>
                    <span className="text-lg font-semibold text-white">{data.avgTime.toFixed(2)} sec</span>
                  </div>
                ))}
              </div>
              {renderEndTrend(sheetData.timeByEnd, 'avgTime', 's', 'Time (seconds)')}
            </div>

            {/* Time by Zone Scatter Plot */}
            {(practiceTimeData.filter(d => d.zone >= 1 && d.zone <= 16).length > 0 || gameTimeData.filter(d => d.zone >= 1 && d.zone <= 16).length > 0) && (() => {
              // A cubic spline through each zone's average time (excluding the
              // takeout weight zones 14-16, same range the old regression
              // used), rather than one straight line through every raw shot —
              // time doesn't change at a constant rate from a guard to the
              // back of the house, so a line either undershoots the middle
              // zones or overshoots the ends.
              const timeByZone = sheetData.zoneData
                .filter(z => z.totalShots > 0 && z.zone >= 1 && z.zone <= 13)
                .map(z => ({ x: z.zone, y: z.avgTime }));
              const timeCurve = fitSmoothCurve(timeByZone);

              if (!timeCurve) return null;

              // Prepare filtered data
              const filteredGameTime = gameTimeData.filter(d => d.zone >= 1 && d.zone <= 16);
              const filteredPracticeTime = practiceTimeData.filter(d => d.zone >= 1 && d.zone <= 16);

              // Fit the y-axis to the actual times instead of a fixed 0-20s
              // scale, which compresses this sheet's real range (often a
              // narrow band well above 0) into a sliver.
              const allTimes = [...filteredGameTime, ...filteredPracticeTime].map(d => d.time);
              const timeMin = Math.min(...allTimes);
              const timeMax = Math.max(...allTimes);
              const timePad = Math.max((timeMax - timeMin) * 0.15, 0.5);

              const trendTimes = timeByZone.map(p => p.y);
              const trendMin = Math.min(...trendTimes);
              const trendMax = Math.max(...trendTimes);
              const trendPad = Math.max((trendMax - trendMin) * 0.15, 0.5);

              return (
              <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6 mt-6">
                <h3 className="text-lg font-semibold text-white mb-4">Time by Zone</h3>
                <p className="text-xs text-slate-400 mb-4">Distribution of hog line times across different zones</p>
                <ResponsiveContainer width="100%" height={450}>
                  <ScatterChart margin={{ top: 20, right: 30, bottom: 120, left: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="zone"
                      name="Zone"
                      type="number"
                      domain={[1, 16]}
                      ticks={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16]}
                      tick={{ fill: '#ffffff' }}
                      label={{ value: 'Zone', position: 'insideBottom', offset: 10, fill: '#ffffff' }}
                    />
                    <YAxis
                      dataKey="time"
                      name="Time"
                      unit=" s"
                      domain={[timeMin - timePad, timeMax + timePad]}
                      tickFormatter={(v: number) => v.toFixed(1)}
                      tick={{ fill: '#ffffff' }}
                      label={{ value: 'Time (seconds)', angle: -90, position: 'insideLeft', offset: -10, fill: '#ffffff' }}
                    />
                    <Tooltip
                      cursor={{ strokeDasharray: '3 3' }}
                      content={({ payload }) => {
                        if (payload && payload.length > 0) {
                          const data = payload[0].payload;
                          const zoneLabel = zoneLabels.find(z => z.zone === data.zone);
                          const isPractice = typeof data.id === 'string' && data.id.startsWith('time-') && parseInt(data.id.split('-')[1]) < practiceCount;
                          return (
                            <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-lg">
                              <p className="font-semibold text-sm">{zoneLabel?.label || `Zone ${data.zone}`}</p>
                              <p className="text-sm text-gray-600">Zone: {data.zone}</p>
                              <p className="text-sm text-gray-600">Time: {data.time.toFixed(2)} sec</p>
                              <p className="text-xs text-gray-500 mt-1">{isPractice ? 'Practice Shot' : 'Game Shot'}</p>
                            </div>
                          );
                        }
                        return null;
                      }}
                    />
                    <Scatter
                      name="Game Shots"
                      data={filteredGameTime}
                      fill="#10b981"
                      fillOpacity={0.6}
                      isAnimationActive={false}
                    />
                    <Scatter
                      name="Practice Shots"
                      data={filteredPracticeTime}
                      fill="#f59e0b"
                      fillOpacity={0.6}
                      isAnimationActive={false}
                    />
                    <Legend wrapperStyle={{ color: '#ffffff' }} />
                  </ScatterChart>
                </ResponsiveContainer>

                {/* Time Trend Line Chart */}
                <div className="mt-4">
                  <h4 className="text-md font-semibold text-white mb-2">Time Trend Line</h4>
                  <p className="text-xs text-slate-400 mb-4">Smoothed curve through each zone's average time, from zone 1 to 13</p>
                  {(() => {
                    // Same house-ring shading as the Curl Trend Line, but
                    // keyed to this chart's actual zone semantics: zone 7 is
                    // the tee line (the house's center), 4 is the front of
                    // the 12-ft ring and 10 the back — both 3 zones from
                    // center, so unlike the Curl Trend Line's range axis this
                    // one is symmetric and both sides share the same scale.
                    const center = 7;
                    const frontEdge = 4;
                    const backEdge = 10;
                    const span = center - frontEdge; // === backEdge - center
                    const frontX = (radiusFt: number) => center - span * (radiusFt / 6);
                    const backX = (radiusFt: number) => center + span * (radiusFt / 6);

                    return (
                      <ResponsiveContainer width="100%" height={300}>
                        <ScatterChart margin={{ top: 20, right: 30, bottom: 85, left: 60 }}>
                          <CartesianGrid strokeDasharray="3 3" />
                          <ReferenceArea x1={frontX(6)} x2={frontX(4)} fill="#2563eb" fillOpacity={0.25} ifOverflow="visible" />
                          <ReferenceArea x1={backX(4)} x2={backX(6)} fill="#2563eb" fillOpacity={0.25} ifOverflow="visible" />
                          <ReferenceArea x1={frontX(4)} x2={frontX(2)} fill="#e2e8f0" fillOpacity={0.14} ifOverflow="visible" />
                          <ReferenceArea x1={backX(2)} x2={backX(4)} fill="#e2e8f0" fillOpacity={0.14} ifOverflow="visible" />
                          <ReferenceArea x1={frontX(2)} x2={frontX(0.5)} fill="#dc2626" fillOpacity={0.3} ifOverflow="visible" />
                          <ReferenceArea x1={backX(0.5)} x2={backX(2)} fill="#dc2626" fillOpacity={0.3} ifOverflow="visible" />
                          <ReferenceArea x1={frontX(0.5)} x2={backX(0.5)} fill="#ffffff" fillOpacity={0.55} ifOverflow="visible" />
                          <XAxis
                            dataKey="zone"
                            name="Zone"
                            type="number"
                            domain={[1, 16]}
                            ticks={[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16]}
                            tick={{ fill: '#ffffff' }}
                            label={{ value: 'Zone', position: 'insideBottom', offset: -5, fill: '#ffffff' }}
                          />
                          <YAxis
                            dataKey="time"
                            name="Time"
                            unit=" s"
                            domain={[trendMin - trendPad, trendMax + trendPad]}
                            tickFormatter={(v: number) => v.toFixed(1)}
                            tick={{ fill: '#ffffff' }}
                            label={{ value: 'Time (seconds)', angle: -90, position: 'insideLeft', offset: -10, fill: '#ffffff' }}
                          />
                          <Tooltip
                            cursor={{ strokeDasharray: '3 3' }}
                            content={({ payload }) => {
                              if (payload && payload.length > 0) {
                                const data = payload[0].payload;
                                return (
                                  <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-lg">
                                    <p className="text-sm text-gray-600">Zone: {data.zone.toFixed(1)}</p>
                                    <p className="text-sm text-gray-600">Predicted Time: {data.time.toFixed(2)} s</p>
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                          <Scatter
                            name="Best Fit Line"
                            data={Array.from({ length: 121 }, (_, i) => {
                              const zone = 1 + (i / 120) * 12;
                              return { zone, time: timeCurve(zone) };
                            })}
                            fill="#ef4444"
                            line={{ stroke: '#ef4444', strokeWidth: 3 }}
                            lineType="joint"
                            shape={(props: any) => {
                              return <circle cx={props.cx} cy={props.cy} r={0} fill="none" />;
                            }}
                            isAnimationActive={false}
                          />
                        </ScatterChart>
                      </ResponsiveContainer>
                    );
                  })()}
                </div>
              </div>
              );
            })()}

            {/* Curl by End */}
            <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6 mt-6">
              <h3 className="text-lg font-semibold text-white mb-4">Curl by End</h3>
              <p className="text-xs text-slate-400 mb-3">Average curl for 4-foot zones per end</p>
              <div className="space-y-2 mb-4">
                {sheetData.curlByEnd.map((data) => (
                  <div key={data.end} className="flex items-center justify-between py-2 border-b border-white/10 last:border-0">
                    <span className="text-sm font-medium text-white">End {data.end}</span>
                    <span className="text-lg font-semibold text-white">{curlDisplay(data.avgCurl)}</span>
                  </div>
                ))}
              </div>
              {renderEndTrend(sheetData.curlByEnd, 'avgCurl', 'ft', 'Curl (feet)')}
            </div>

            {/* Curl by Path — kept separate per end, not blended */}
            {renderCurlByPathCard(curlByPositionAwayData, 'Away')}
            {renderCurlByPathCard(curlByPositionHomeData, 'Home')}

            {/* Zone Analysis Table */}
            <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 mt-6 overflow-hidden">
              <div className="p-6 border-b border-white/15">
                <h3 className="text-lg font-semibold text-white">Zone Analysis</h3>
                <p className="text-sm text-blue-300 mt-1">Ice behavior for each zone on the sheet</p>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-white/5 border-b border-white/15">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-blue-300 uppercase tracking-wider">Zone</th>
                      <th className="px-6 py-3 text-center text-xs font-semibold text-blue-300 uppercase tracking-wider">Shots</th>
                      <th className="px-6 py-3 text-center text-xs font-semibold text-blue-300 uppercase tracking-wider">Avg Curl</th>
                      <th className="px-6 py-3 text-center text-xs font-semibold text-blue-300 uppercase tracking-wider">Curl Trend</th>
                      <th className="px-6 py-3 text-center text-xs font-semibold text-blue-300 uppercase tracking-wider">Avg Time</th>
                      <th className="px-6 py-3 text-center text-xs font-semibold text-blue-300 uppercase tracking-wider">Time Trend</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    {sheetData.zoneData && sheetData.zoneData.map((zone, idx) => (
                      <tr key={zone.zone} className={`${zone.totalShots === 0 ? 'opacity-40' : 'hover:bg-white/5'} ${idx % 2 === 0 ? 'bg-white/5' : ''}`}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-white">
                            <span className="text-blue-300">{zone.zone}</span> - {zone.label}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="text-sm text-white">{zone.totalShots}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="text-sm font-semibold text-white">
                            {zone.totalShots > 0 ? curlDisplay(zone.avgCurl) : '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {zone.totalShots > 0 ? (
                            <div className="flex items-center justify-center gap-2">
                              <div className={`p-1 rounded ${getTrendBgColor(zone.curlTrend)}`}>
                                {zone.curlTrend === 'stable' ? (
                                  <Minus className={`w-3 h-3 ${getTrendColor(zone.curlTrend)}`} />
                                ) : zone.curlTrend === 'increases' ? (
                                  <TrendingUp className={`w-3 h-3 ${getTrendColor(zone.curlTrend)}`} />
                                ) : (
                                  <TrendingDown className={`w-3 h-3 ${getTrendColor(zone.curlTrend)}`} />
                                )}
                              </div>
                              <span className={`text-xs font-medium ${getTrendColor(zone.curlTrend)}`}>
                                {formatTrend(zone.curlTrend)}
                              </span>
                            </div>
                          ) : (
                            <div className="text-center text-sm text-slate-400">-</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="text-sm font-semibold text-white">
                            {zone.totalShots > 0 ? `${zone.avgTime.toFixed(2)} s` : '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {zone.totalShots > 0 ? (
                            <div className="flex items-center justify-center gap-2">
                              <div className={`p-1 rounded ${getTrendBgColor(zone.timeTrend)}`}>
                                {zone.timeTrend === 'stable' ? (
                                  <Minus className={`w-3 h-3 ${getTrendColor(zone.timeTrend)}`} />
                                ) : zone.timeTrend === 'slows-down' ? (
                                  <TrendingUp className={`w-3 h-3 ${getTrendColor(zone.timeTrend)}`} />
                                ) : (
                                  <TrendingDown className={`w-3 h-3 ${getTrendColor(zone.timeTrend)}`} />
                                )}
                              </div>
                              <span className={`text-xs font-medium ${getTrendColor(zone.timeTrend)}`}>
                                {formatTrend(zone.timeTrend)}
                              </span>
                            </div>
                          ) : (
                            <div className="text-center text-sm text-slate-400">-</div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Broom Position by Rotation — split into Away and Home sub-tables */}
            <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 mt-6 overflow-hidden">
              <div className="p-6 border-b border-white/15">
                <h3 className="text-lg font-semibold text-white">Broom Position by Rotation</h3>
                <p className="text-sm text-blue-300 mt-1">Horizontal broom placement across the house (for shots aimed at zones 4-10)</p>
              </div>

              {/* Away Ends sub-table */}
              <div className="px-6 pt-5 pb-2">
                <h4 className="text-sm font-semibold text-sky-300 mb-1">Away Ends (Odd)</h4>
              </div>
              {renderBroomPositionTable(sheetData.broomPositionDataAway)}

              {/* Home Ends sub-table */}
              <div className="px-6 pt-5 pb-2 border-t border-white/15 mt-2">
                <h4 className="text-sm font-semibold text-green-300 mb-1">Home Ends (Even)</h4>
              </div>
              {renderBroomPositionTable(sheetData.broomPositionDataHome)}
            </div>

            {/* Insights */}
            <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-6 mt-6">
              <h3 className="text-lg font-semibold text-white mb-4">Sheet Insights</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <h4 className="font-medium text-white mb-2">Ice Curl Behavior:</h4>
                  <ul className="space-y-1 text-blue-300">
                    {sheetData.curlTrend === 'increases' && (
                      <li>• Ice path is wearing down, causing rocks to curl more</li>
                    )}
                    {sheetData.curlTrend === 'decreases' && (
                      <li>• Debris or frost buildup may be reducing curl</li>
                    )}
                    {sheetData.curlTrend === 'stable' && (
                      <li>• Ice conditions are consistent throughout the game</li>
                    )}
                    <li>• Average curl of {curlDisplay(sheetData.avgCurl)} suggests {Math.abs(sheetData.avgCurl) > 4 ? 'good' : 'moderate'} curling conditions</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-medium text-white mb-2">Ice Speed Behavior:</h4>
                  <ul className="space-y-1 text-blue-300">
                    {sheetData.timeTrend === 'speeds-up' && (
                      <li>• Ice is getting faster as the game progresses</li>
                    )}
                    {sheetData.timeTrend === 'slows-down' && (
                      <li>• Ice is slowing down, possibly due to temperature changes</li>
                    )}
                    {sheetData.timeTrend === 'stable' && (
                      <li>• Ice speed is consistent throughout the game</li>
                    )}
                    <li>• Consider adjusting weight expectations for later ends</li>
                  </ul>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="bg-white/8 backdrop-blur-sm border border-white/15 rounded-2xl shadow-xl shadow-black/20 p-12 text-center">
            <p className="text-blue-300 text-lg">No shot data available yet</p>
            <p className="text-slate-400 text-sm mt-2">Start recording shots to see sheet behavior</p>
          </div>
        )}
      </div>
    </div>
  );
}
